import React, { useState, useEffect, useRef } from "react";
import i18n from "@/utils/i18n";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMapEvents,
} from "react-leaflet";
import { MapPin } from "@/components/icons";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./index.scss";

// Leaflet marker icon'larını düzelt
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

interface MapComponentProps {
  center?: { lat: number; lng: number };
  zoom?: number;
  onLocationSelect?: (location: {
    lat: number;
    lng: number;
    address: string;
  }) => void;
  selectedLocation?: { lat: number; lng: number; address: string };
  height?: string;
  showDirections?: boolean;
  directionsOrigin?: { lat: number; lng: number };
  directionsDestination?: { lat: number; lng: number };
  searchQuery?: string;
  onMapReady?: (map: any) => void;
}

// Map click handler component
const MapClickHandler: React.FC<{
  onLocationSelect?: (location: {
    lat: number;
    lng: number;
    address: string;
  }) => void;
}> = ({ onLocationSelect }) => {
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  useMapEvents({
    click: (e: any) => {
      if (!onLocationSelect) return;
      const { lat, lng } = e.latlng;
      const fallback = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      // Marker'ın anında görünmesi için önce koordinatla bildir
      onLocationSelect({ lat, lng, address: fallback });

      // Nominatim reverse geocoding — adres gelince tekrar bildir
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=0`,
        {
          signal: abortRef.current.signal,
          headers: { "Accept-Language": i18n.language || "tr" },
        },
      )
        .then((r) => r.json())
        .then((data: { display_name?: string }) => {
          if (data && data.display_name) {
            onLocationSelect({ lat, lng, address: data.display_name });
          }
        })
        .catch(() => {
          // abort ya da network hatası — fallback kalır
        });
    },
  });
  return null;
};

const MapComponent: React.FC<MapComponentProps> = ({
  center = { lat: 39.9334, lng: 32.8597 }, // Ankara default
  zoom = 13,
  onLocationSelect,
  selectedLocation,
  height = "400px",
  showDirections = false,
  directionsOrigin,
  directionsDestination,
  searchQuery,
  onMapReady,
}) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [map, setMap] = useState<any>(null);

  useEffect(() => {
    // Harita yükleme simülasyonu
    const timer = setTimeout(() => {
      setMapLoaded(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Map ready handler
  const handleMapReady = (mapInstance: any) => {
    setMap(mapInstance);
    if (onMapReady) {
      onMapReady(mapInstance);
    }
  };

  // Search query değiştiğinde haritayı o konuma taşı
  useEffect(() => {
    if (map && searchQuery && searchQuery.trim()) {
      // Basit geocoding simülasyonu - gerçek uygulamada Nominatim API kullanılabilir
      const searchLocation = getLocationFromQuery(searchQuery);
      if (searchLocation) {
        map.setView([searchLocation.lat, searchLocation.lng], 15);
      }
    }
  }, [map, searchQuery]);

  // Basit arama fonksiyonu (gerçek uygulamada Nominatim API kullanılmalı)
  const getLocationFromQuery = (query: string) => {
    const locations: Record<string, { lat: number; lng: number }> = {
      ankara: { lat: 39.9334, lng: 32.8597 },
      istanbul: { lat: 41.0082, lng: 28.9784 },
      izmir: { lat: 38.4192, lng: 27.1287 },
      bursa: { lat: 40.1826, lng: 29.0665 },
      antalya: { lat: 36.8969, lng: 30.7133 },
      kadıköy: { lat: 40.9906, lng: 29.0267 },
      beşiktaş: { lat: 41.0426, lng: 29.0075 },
      çankaya: { lat: 39.9208, lng: 32.8541 },
    };

    const lowerQuery = query.toLowerCase();
    for (const [key, location] of Object.entries(locations)) {
      if (lowerQuery.includes(key)) {
        return location;
      }
    }
    return null;
  };

  const containerStyle = {
    width: "100%",
    height: height,
  };

  if (!mapLoaded) {
    return (
      <div className="map-component">
        <div className="map-loading" style={containerStyle}>
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>{i18n.t("mapWidget.loading")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="map-component">
      <MapContainer
        center={[center.lat, center.lng] as [number, number]}
        zoom={zoom}
        style={containerStyle}
        className="leaflet-container"
        ref={(mapRef) => {
          if (mapRef) {
            handleMapReady(mapRef);
          }
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <MapClickHandler onLocationSelect={onLocationSelect} />

        {selectedLocation && (
          <Marker
            position={
              [selectedLocation.lat, selectedLocation.lng] as [number, number]
            }
            icon={L.divIcon({
              className: "custom-marker",
              html: `<div class="custom-marker-content">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 40 40" fill="none">
<path d="M20 2.5C16.3545 2.50414 12.8596 3.95412 10.2818 6.53185C7.70412 9.10958 6.25414 12.6045 6.25 16.25C6.25 28.0156 18.75 36.9016 19.2828 37.2734C19.493 37.4207 19.7434 37.4996 20 37.4996C20.2566 37.4996 20.507 37.4207 20.7172 37.2734C21.25 36.9016 33.75 28.0156 33.75 16.25C33.7459 12.6045 32.2959 9.10958 29.7182 6.53185C27.1404 3.95412 23.6455 2.50414 20 2.5ZM20 11.25C20.9889 11.25 21.9556 11.5432 22.7779 12.0927C23.6001 12.6421 24.241 13.423 24.6194 14.3366C24.9978 15.2502 25.0969 16.2555 24.9039 17.2254C24.711 18.1954 24.2348 19.0863 23.5355 19.7855C22.8363 20.4848 21.9454 20.961 20.9755 21.1539C20.0055 21.3469 19.0002 21.2478 18.0866 20.8694C17.173 20.491 16.3921 19.8501 15.8427 19.0279C15.2932 18.2056 15 17.2389 15 16.25C15 14.9239 15.5268 13.6521 16.4645 12.7145C17.4021 11.7768 18.6739 11.25 20 11.25Z" fill="url(#paint0_linear_476_2273)"/>
<circle cx="20.15" cy="16.15" r="5.15" fill="white"/>
<defs>
<linearGradient id="paint0_linear_476_2273" x1="8.5" y1="3" x2="34" y2="37" gradientUnits="userSpaceOnUse">
<stop stop-color="#e7212b"/>
<stop offset="1" stop-color="#FFB270"/>
</linearGradient>
</defs>
</svg>
                </div>`,
              iconSize: [32, 32],
              iconAnchor: [16, 32],
            })}
          />
        )}

        {showDirections && directionsOrigin && (
          <Marker
            position={
              [directionsOrigin.lat, directionsOrigin.lng] as [number, number]
            }
          >
            <Popup>
              <div className="marker-popup">
                <strong>{i18n.t("mapWidget.startPoint")}</strong>
              </div>
            </Popup>
          </Marker>
        )}

        {showDirections && directionsDestination && (
          <Marker
            position={
              [directionsDestination.lat, directionsDestination.lng] as [
                number,
                number,
              ]
            }
          >
            <Popup>
              <div className="marker-popup">
                <strong>Hedef Nokta</strong>
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>

      {selectedLocation && (
        <div className="selected-location-info">
          <MapPin width={16} height={16} />
          <span>{selectedLocation.address}</span>
        </div>
      )}
    </div>
  );
};

export default MapComponent;
